/**
 * Statistics (STA-001, owner 2026-09-25): Wordle's four numbers, a result chart,
 * average against par, and the countdown to the next table.
 *
 * Computed from the device's day records (daily/history.ts) every time, never stored,
 * so they can never drift from the history they describe. Days are the SERVER's day
 * keys (D-028), so a device clock set forward cannot add a day to a streak.
 *
 * Only daily tables' attempts that count are in the history: practice is never saved,
 * and archive plays (DLY-005) will be kept apart, since they never count (D-008).
 */
import { SCORE_LABELS } from '../config/site.ts';
import type { DayRecord } from '../daily/history.ts';
import { resultLabel } from '../ui/share.ts';

export interface ResultBar {
  readonly label: string;
  readonly count: number;
}

export interface Stats {
  /** Days with a shot taken, once decided: cleared, or their day is over. */
  readonly played: number;
  readonly cleared: number;
  /** Whole percent of played days that were cleared; 0 with nothing played. */
  readonly clearedPercent: number;
  /** Consecutive days cleared, ending today, or yesterday while today is still open. */
  readonly currentStreak: number;
  readonly bestStreak: number;
  /** Cleared days by result label, in the order the labels are defined (best first). */
  readonly results: readonly ResultBar[];
  /** Mean of (strokes − par) over cleared days; undefined with none cleared. */
  readonly averageVsPar: number | undefined;
  /** Today's result label, once today is cleared: the bar to highlight. */
  readonly todayLabel: string | undefined;
}

const MS_PER_DAY = 86_400_000;

/** The day key after `dateKey`. UTC calendar arithmetic: no zone, so no DST surprises. */
export function nextDayKey(dateKey: string): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  return new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1) + MS_PER_DAY).toISOString().slice(0, 10);
}

const isCleared = (record: DayRecord): boolean => record.strokes !== undefined;

/**
 * `todayKey` is the server's day (undefined before the daily game has started). A day
 * that is not cleared counts as played only once it is over: today's half-played table
 * is still open, and marking it a failure mid-game would be wrong.
 */
export function computeStats(records: readonly DayRecord[], todayKey: string | undefined): Stats {
  const days = [...records].sort((a, b) => (a.dateKey < b.dateKey ? -1 : 1));
  const decided = days.filter(
    (r) => r.shots.length > 0 && (isCleared(r) || todayKey === undefined || r.dateKey < todayKey),
  );
  const cleared = decided.filter(isCleared);

  // Streaks: runs of cleared days with no gap between them.
  let bestStreak = 0;
  let run = 0;
  let previous: string | undefined;
  for (const record of cleared) {
    run = previous !== undefined && nextDayKey(previous) === record.dateKey ? run + 1 : 1;
    if (run > bestStreak) bestStreak = run;
    previous = record.dateKey;
  }
  // The latest run is still current only if it reaches today, or yesterday while today
  // is still open to be cleared.
  let currentStreak = 0;
  if (previous !== undefined) {
    const reachesNow =
      todayKey === undefined || previous === todayKey || nextDayKey(previous) === todayKey;
    currentStreak = reachesNow ? run : 0;
  }

  const counts = new Map<string, number>();
  let deltaSum = 0;
  for (const record of cleared) {
    const strokes = record.strokes ?? 0;
    const label = resultLabel(strokes, record.par);
    counts.set(label, (counts.get(label) ?? 0) + 1);
    deltaSum += strokes - record.par;
  }
  const today = todayKey === undefined ? undefined : cleared.find((r) => r.dateKey === todayKey);

  return {
    played: decided.length,
    cleared: cleared.length,
    clearedPercent: decided.length === 0 ? 0 : Math.round((cleared.length / decided.length) * 100),
    currentStreak,
    bestStreak,
    results: SCORE_LABELS.map(({ label }) => ({ label, count: counts.get(label) ?? 0 })),
    averageVsPar: cleared.length === 0 ? undefined : deltaSum / cleared.length,
    todayLabel: today === undefined ? undefined : resultLabel(today.strokes ?? 0, today.par),
  };
}

/** "+1.4", "−0.3", or "Level" (U+2212 minus, as a score would be printed). */
export function formatAverage(average: number, level: string): string {
  const tenths = Math.round(average * 10) / 10;
  if (tenths === 0) return level;
  return `${tenths > 0 ? '+' : '−'}${Math.abs(tenths).toFixed(1)}`;
}

/** "3:05:09" under a day, "2d 3:05:09" over one. Never negative. */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(total / 86_400);
  const hours = Math.floor((total % 86_400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const clock = `${String(hours)}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  return days > 0 ? `${String(days)}d ${clock}` : clock;
}

/**
 * The server's clock as best this page knows it: the time the server reported, moved on
 * by how long this page has been open. `receivedAtMs` is the device clock when the reply
 * arrived, so a device clock that is wrong by days is corrected for, not trusted (D-027).
 */
export function serverClock(
  serverNowMs: number,
  receivedAtMs: number,
  deviceNow: () => number,
): () => number {
  const offset = serverNowMs - receivedAtMs;
  return () => deviceNow() + offset;
}
